import React, { useEffect, useState, useMemo } from 'react';
import API from '../api';
import './InvoiceList.css';
import { FaFileInvoiceDollar, FaSearch, FaChevronRight } from 'react-icons/fa';
import Pagination from '../components/Pagination';

const DATE_FILTERS = [
  { key: 'all',       label: 'All Time' },
  { key: 'today',     label: 'Today' },
  { key: 'thisWeek',  label: 'This Week' },
  { key: 'lastWeek',  label: 'Last Week' },
  { key: 'lastMonth', label: 'Last Month' },
  { key: 'custom',    label: 'Custom Range' },
];

function getDateRange(key) {
  const now = new Date();
  const startOfDay = (d) => { d.setHours(0, 0, 0, 0); return d; };
  const endOfDay   = (d) => { d.setHours(23, 59, 59, 999); return d; };

  if (key === 'today') {
    return { from: startOfDay(new Date(now)), to: endOfDay(new Date(now)) };
  }
  if (key === 'thisWeek') {
    const day = now.getDay(); // 0=Sun
    const diff = (day === 0 ? -6 : 1 - day); // Monday
    const monday = new Date(now);
    monday.setDate(now.getDate() + diff);
    return { from: startOfDay(monday), to: endOfDay(new Date(now)) };
  }
  if (key === 'lastWeek') {
    const day = now.getDay();
    const diff = (day === 0 ? -6 : 1 - day);
    const thisMonday = new Date(now);
    thisMonday.setDate(now.getDate() + diff);
    const lastMonday = new Date(thisMonday);
    lastMonday.setDate(thisMonday.getDate() - 7);
    const lastSunday = new Date(thisMonday);
    lastSunday.setDate(thisMonday.getDate() - 1);
    return { from: startOfDay(lastMonday), to: endOfDay(lastSunday) };
  }
  if (key === 'lastMonth') {
    const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const last  = new Date(now.getFullYear(), now.getMonth(), 0);
    return { from: startOfDay(first), to: endOfDay(last) };
  }
  return null; // 'all'
}

function calcGrandTotal(inv) {
  const base = (inv.serviceTotal || 0) + (inv.miscTotal || 0) + (inv.productsTotal || 0);
  const tax  = (base * (inv.taxPercent || 0)) / 100;
  return base + tax + (inv.tipAmount || 0);
}

function InvoiceList({ onOpenInvoice, user }) {
  const [invoices,  setInvoices]  = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState('');

  const [dateFilter,     setDateFilter]     = useState('all');
  const [employeeFilter, setEmployeeFilter] = useState('');
  const [search,         setSearch]         = useState('');
  const [customFrom,     setCustomFrom]     = useState('');
  const [customTo,       setCustomTo]       = useState('');
  const [ilPage,         setIlPage]         = useState(1);
  const [ilPageSize,     setIlPageSize]     = useState(25);

  const businessId = localStorage.getItem('id');
  const isAdmin    = user?.role === 'admin';

  // Fetch invoices + employees
  useEffect(() => {
    if (!businessId) return;

    const fetchAll = async () => {
      try {
        const [invRes, empRes] = await Promise.allSettled([
          API.get(`/auth/invoices?businessId=${businessId}`),
          API.get(`/auth/employees/business/${businessId}`),
        ]);

        if (invRes.status === 'fulfilled') {
          setInvoices(invRes.value.data || []);
        }
        if (empRes.status === 'fulfilled') {
          setEmployees(empRes.value.data?.employees || empRes.value.data || []);
        }
      } catch (err) {
        setError('Failed to load invoices.');
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, [businessId]);

  // Employee lookup map
  const empMap = useMemo(() => {
    const m = {};
    employees.forEach((e) => { m[e._id] = e.name || e.firstName || 'Employee'; });
    return m;
  }, [employees]);

  // Non-admin: filter to own invoices only
  const ownEmployeeId = !isAdmin ? (user?.employeeId || user?.id) : null;

  // Apply all filters
  const filtered = useMemo(() => {
    let list = isAdmin ? invoices : invoices.filter(
      (inv) => inv.employeeId && String(inv.employeeId) === String(ownEmployeeId)
    );

    // Date
    if (dateFilter === 'custom' && (customFrom || customTo)) {
      list = list.filter((inv) => {
        const d = new Date(inv.createdAt);
        if (customFrom) {
          const from = new Date(customFrom); from.setHours(0, 0, 0, 0);
          if (d < from) return false;
        }
        if (customTo) {
          const to = new Date(customTo); to.setHours(23, 59, 59, 999);
          if (d > to) return false;
        }
        return true;
      });
    } else {
      const range = getDateRange(dateFilter);
      if (range) {
        list = list.filter((inv) => {
          const d = new Date(inv.createdAt);
          return d >= range.from && d <= range.to;
        });
      }
    }

    // Employee (admin only)
    if (isAdmin && employeeFilter) {
      list = list.filter((inv) => String(inv.employeeId) === employeeFilter);
    }

    // Search (customer name or invoice number)
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(
        (inv) =>
          (inv.customerName || '').toLowerCase().includes(q) ||
          inv._id.slice(0, 16).toUpperCase().includes(q.toUpperCase())
      );
    }

    return list.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [invoices, dateFilter, customFrom, customTo, employeeFilter, search, isAdmin, ownEmployeeId]);

  // Reset to page 1 whenever filters change
  useEffect(() => { setIlPage(1); }, [dateFilter, customFrom, customTo, employeeFilter, search]);

  const paginatedInvoices = useMemo(
    () => filtered.slice((ilPage - 1) * ilPageSize, ilPage * ilPageSize),
    [filtered, ilPage, ilPageSize]
  );

  // Totals for summary bar
  const totalRevenue = useMemo(
    () => filtered.reduce((s, inv) => s + calcGrandTotal(inv), 0),
    [filtered]
  );

  const formatDate = (iso) =>
    new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  if (loading) {
    return (
      <div className="il-page">
        <div className="il-loading">Loading invoices…</div>
      </div>
    );
  }

  return (
    <div className="il-page">

      {/* ── Page header ── */}
      <div className="il-header">
        <div>
          <h2 className="il-title">Invoices</h2>
          <p className="il-subtitle">View and manage all invoices</p>
        </div>
      </div>

      {error && <div className="il-error">{error}</div>}

      {/* ── Filters ── */}
      <div className="il-filters">

        {/* Date pills */}
        <div className="il-date-pills">
          {DATE_FILTERS.map((f) => (
            <button
              key={f.key}
              className={`il-pill ${dateFilter === f.key ? 'active' : ''}`}
              onClick={() => setDateFilter(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Custom date range inputs */}
        {dateFilter === 'custom' && (
          <div className="il-custom-range">
            <input
              type="date"
              className="il-date-input"
              value={customFrom}
              onChange={(e) => setCustomFrom(e.target.value)}
              placeholder="From"
            />
            <span className="il-custom-range-sep">→</span>
            <input
              type="date"
              className="il-date-input"
              value={customTo}
              onChange={(e) => setCustomTo(e.target.value)}
              placeholder="To"
            />
          </div>
        )}

        <div className="il-filters-right">
          {/* Employee filter (admin only) */}
          {isAdmin && employees.length > 0 && (
            <select
              className="il-select"
              value={employeeFilter}
              onChange={(e) => setEmployeeFilter(e.target.value)}
            >
              <option value="">All Employees</option>
              {employees.map((emp) => (
                <option key={emp._id} value={emp._id}>
                  {emp.name || emp.firstName || 'Employee'}
                </option>
              ))}
            </select>
          )}

          {/* Search */}
          <div className="il-search-wrap">
            <FaSearch className="il-search-icon" />
            <input
              className="il-search"
              placeholder="Search customer or invoice #"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* ── Summary bar ── */}
      <div className="il-summary-bar">
        <div className="il-summary-item">
          <span className="il-summary-label">Total Invoices</span>
          <span className="il-summary-value">{filtered.length}</span>
        </div>
        <div className="il-summary-item">
          <span className="il-summary-label">Total Revenue</span>
          <span className="il-summary-value">${totalRevenue.toFixed(2)}</span>
        </div>
        <div className="il-summary-item">
          <span className="il-summary-label">Paid</span>
          <span className="il-summary-value il-summary-paid">
            {filtered.filter((i) => i.status === 'Paid').length}
          </span>
        </div>
        <div className="il-summary-item">
          <span className="il-summary-label">Pending</span>
          <span className="il-summary-value il-summary-pending">
            {filtered.filter((i) => i.status === 'Pending').length}
          </span>
        </div>
      </div>

      {/* ── Table ── */}
      <div className="il-card">
        {filtered.length === 0 ? (
          <div className="il-empty">
            <FaFileInvoiceDollar className="il-empty-icon" />
            <p>No invoices found for the selected filters.</p>
          </div>
        ) : (
          <table className="il-table">
            <thead>
              <tr>
                <th>Invoice #</th>
                <th>Customer</th>
                {isAdmin && <th>Employee</th>}
                <th>Date</th>
                <th>Payment</th>
                <th>Total</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {paginatedInvoices.map((inv) => (
                <tr
                  key={inv._id}
                  className="il-row"
                  onClick={() => onOpenInvoice(inv._id)}
                >
                  <td className="il-inv-num">
                    #{inv._id.slice(-8).toUpperCase()}
                  </td>
                  <td>
                    <div className="il-customer-name">{inv.customerName || '—'}</div>
                    {inv.customerEmail && (
                      <div className="il-customer-email">{inv.customerEmail}</div>
                    )}
                  </td>
                  {isAdmin && (
                    <td className="il-employee">
                      {inv.employeeId ? (empMap[inv.employeeId] || '—') : '—'}
                    </td>
                  )}
                  <td className="il-date">{formatDate(inv.createdAt)}</td>
                  <td>
                    <span className={`il-payment-badge il-payment-${inv.paymentMethod}`}>
                      {inv.paymentMethod || '—'}
                    </span>
                  </td>
                  <td className="il-total">${calcGrandTotal(inv).toFixed(2)}</td>
                  <td>
                    <span className={`il-status-badge il-status-${(inv.status || 'Pending').toLowerCase()}`}>
                      {inv.status || 'Pending'}
                    </span>
                  </td>
                  <td className="il-arrow">
                    <FaChevronRight />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <Pagination
          page={ilPage}
          totalPages={Math.max(1, Math.ceil(filtered.length / ilPageSize))}
          totalItems={filtered.length}
          pageSize={ilPageSize}
          onPageChange={setIlPage}
          onPageSizeChange={(s) => { setIlPageSize(s); setIlPage(1); }}
        />
      </div>

    </div>
  );
}

export default InvoiceList;
